import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { CrmActionQueueManualResolutionPolicy, CrmActionQueueMaterializationCapability } from "./use-crm-action-contracts";
import type { CrmActionObservedOutcome, CrmActionOutcomeScoring } from "./use-crm-event-action-score";

export type CrmActionExecutionQueueStatus =
  | "ready_for_execution"
  | "manual_review"
  | "blocked"
  | "unknown";

export type CrmActionExecutionQueuePriorityTier =
  | "outcome_backed_ready"
  | "ready"
  | "manual_review"
  | "already_materialized"
  | "blocked"
  | "unknown";

export type CrmActionExecutionQueueStaleBucket =
  | "fresh"
  | "aging"
  | "stale"
  | "critical";

export interface CrmActionExecutionQueueFilters {
  status?: CrmActionExecutionQueueStatus;
  priority?: CrmActionExecutionQueuePriorityTier;
  actionRef?: string;
  contractRef?: string;
  sourceEventType?: string;
  signalFamily?: string;
  materializable?: boolean;
  resolution?: "materializable" | "manual_only" | "bridge_blocked";
}

export interface CrmActionExecutionQueueTotals {
  total: number;
  readyForExecution: number;
  manualReview: number;
  blocked: number;
  unknown: number;
  outcomeBackedReady: number;
  materializableReady: number;
  manualOnlyReady: number;
  bridgeBlockedReady: number;
  alreadyMaterialized: number;
}

export interface CrmActionExecutionQueueBucket {
  status?: CrmActionExecutionQueueStatus;
  actionRef?: string;
  total: number;
}

export type CrmActionQueueExecutionEligibilityState =
  | "executable"
  | "manual_only"
  | "already_materialized"
  | "blocked"
  | "bridge_missing"
  | "not_ready";

export interface CrmActionQueueExecutionEligibility {
  state: CrmActionQueueExecutionEligibilityState;
  canExecute: boolean;
  canMaterialize: boolean;
  canResolveManually: boolean;
  alreadyMaterialized: boolean;
  bridgeRegistered: boolean;
  manualResolutionRegistered: boolean;
  requiresStaffGate: boolean;
  materializationEndpoint: string | null;
  operatorAction: string | null;
  blockers: string[];
  mutatesCrmOnExecution: boolean;
}

export interface CrmActionExecutionQueueItem {
  id: string;
  contactId: string | null;
  targetEntityType?: string | null;
  targetEntityId?: string | null;
  recommendationId: string;
  actionRef: string;
  intent: string;
  status: CrmActionExecutionQueueStatus;
  readinessStatus: string;
  executionPlanStatus: string;
  executionPlanMode: string | null;
  executionPlanReason: string | null;
  ageDays?: number;
  ageHours?: number;
  staleBucket?: CrmActionExecutionQueueStaleBucket;
  confidence: number | null;
  priority: {
    tier: CrmActionExecutionQueuePriorityTier;
    score: number;
    reason: string;
  };
  observedOutcome: CrmActionObservedOutcome | null;
  outcomeScoring: CrmActionOutcomeScoring | null;
  queueMaterialization: CrmActionQueueMaterializationCapability | null;
  queueManualResolution: CrmActionQueueManualResolutionPolicy | null;
  materializationBlockers: string[];
  executionEligibility?: CrmActionQueueExecutionEligibility;
  sourceEventId: string | null;
  sourceEventType?: string | null;
  signalFamily?: string | null;
  sourceSurface: string | null;
  suggestedInput: Record<string, unknown> | null;
  materializationEventId: string | null;
  materializationStatus: "created" | "updated" | "existing" | null;
  materializedEntityType: "task" | "person" | "deal" | "activity" | "company" | null;
  materializedEntityId: string | null;
  materializedAt: string | null;
  materializedTaskId: string | null;
  materializedDealId: string | null;
  materializedPersonId: string | null;
  materializedActivityId: string | null;
  materializedCompanyId: string | null;
  canMaterializeTask: boolean;
  canMaterializeTaskUpdate: boolean;
  materializedTaskState: "created" | "updated" | "existing" | null;
  materializedActivityState: "created" | "existing" | null;
  canMaterializeActivity: boolean;
  materializedDealState: "created" | "updated" | "stage_updated" | "existing" | null;
  canMaterializeDeal: boolean;
  materializedTagState: "applied" | "removed" | "existing" | null;
  canMaterializeTag: boolean;
  materializedPersonState: "created" | "updated" | "existing" | null;
  canMaterializePersonCreate: boolean;
  canMaterializePersonUpdate: boolean;
  materializedCompanyState: "updated" | "existing" | null;
  canMaterializeCompanyUpdate: boolean;
  actorId: string;
  actorType: string;
  acceptedAt: string;
  execution: {
    shadowOnly: boolean;
    executesActions: boolean;
    mutatesCrm: boolean;
    requiresStaffGate?: boolean;
    endpoint?: string | null;
  };
}

export interface CrmActionExecutionQueue {
  windowDays: number;
  filters: {
    status: CrmActionExecutionQueueStatus | null;
    priority: CrmActionExecutionQueuePriorityTier | null;
    actionRef: string | null;
    contractRef: string | null;
    sourceEventType: string | null;
    signalFamily: string | null;
    materializable: boolean;
    resolution: "materializable" | "manual_only" | "bridge_blocked" | null;
  };
  totals: CrmActionExecutionQueueTotals;
  filteredTotals: CrmActionExecutionQueueTotals;
  byStatus: CrmActionExecutionQueueBucket[];
  byActionRef: CrmActionExecutionQueueBucket[];
  ageBuckets?: Record<CrmActionExecutionQueueStaleBucket, number>;
  oldestQueuedAt?: string | null;
  oldestAgeDays?: number | null;
  nextExecutable: CrmActionExecutionQueueItem | null;
  items: CrmActionExecutionQueueItem[];
  safety: {
    shadowOnly: boolean;
    executesActions: boolean;
    mutatesCrm: boolean;
  };
}

export interface MaterializeCrmActionTaskResult {
  status: "created" | "existing";
  taskId: string;
  title: string;
  taskStatus: string;
}

export interface MaterializeCrmActionTaskUpdateResult {
  status: "updated" | "existing";
  taskId: string;
  changedFields: string[];
  title?: string | null;
  taskStatus?: string | null;
}

export interface MaterializeCrmActionActivityResult {
  status: "created" | "existing";
  activityId: string;
  type: string;
  subject: string | null;
  personId: string | null;
  dealId: string | null;
  companyId: string | null;
}

export interface MaterializeCrmActionTagResult {
  status: "updated" | "existing";
  personId: string;
  tag: string;
  actionRef: string;
  tags: string[];
}

export interface MaterializeCrmActionDealResult {
  status: "created" | "updated" | "existing";
  dealId: string;
  title: string;
  stageId?: string;
  changedFields?: string[];
  value?: number | null;
}

export interface MaterializeCrmActionPersonUpdateResult {
  status: "updated" | "existing";
  personId: string;
  changedFields: string[];
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  timezone?: string | null;
  tags?: string[];
  source?: string | null;
  sourceDetail?: string | null;
  leadScore?: number | null;
  engagementStatus?: string | null;
  lifecycleStage?: string | null;
  lastActivityAt?: string | null;
  emailVerifiedAt?: string | null;
}

export interface MaterializeCrmActionCompanyUpdateResult {
  status: "updated" | "existing";
  companyId: string;
  changedFields: string[];
  name?: string | null;
  domain?: string | null;
  industry?: string | null;
}

export interface MaterializeCrmActionPersonResult {
  status: "created" | "existing";
  personId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface ResolveCrmActionManualResult {
  status: "resolved" | "existing";
  eventId?: string;
  actionRef: string;
  personId: string;
  mode: "manual_only";
}

export interface BulkMaterializeCrmActionQueueResult {
  dryRun: boolean;
  requested: number;
  planned: number;
  succeeded: number;
  failed: number;
  skipped: number;
  results: Array<{
    decisionEventId: string;
    recommendationId: string;
    actionRef: string;
    intent: string;
    endpoint?: string | null;
    status: "planned" | "succeeded" | "failed" | "skipped";
    reason?: string;
    executionEligibility?: CrmActionQueueExecutionEligibility | null;
    httpStatus?: number;
    resultStatus?: string | null;
    response?: unknown;
  }>;
  safety: {
    staffGated: true;
    usesExplicitMaterializationEndpoints: true;
    mutatesCrm: boolean;
  };
}

export interface BulkResolveManualCrmActionQueueResult {
  dryRun: boolean;
  requested: number;
  planned: number;
  succeeded: number;
  failed: number;
  skipped: number;
  results: Array<{
    decisionEventId: string;
    recommendationId: string;
    actionRef: string;
    intent: string;
    endpoint?: string | null;
    status: "planned" | "succeeded" | "failed" | "skipped";
    reason?: string;
    executionEligibility?: CrmActionQueueExecutionEligibility | null;
    queueManualResolution?: CrmActionQueueManualResolutionPolicy | null;
    httpStatus?: number;
    resultStatus?: string | null;
    response?: unknown;
  }>;
  safety: {
    staffGated: true;
    recordsManualResolutionEvents: boolean;
    mutatesCrm: boolean;
  };
}

export async function materializeCrmActionTask(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionTaskResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionTaskResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-task`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionTaskUpdate(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionTaskUpdateResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionTaskUpdateResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-task-update`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionActivity(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionActivityResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionActivityResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-activity`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionTag(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionTagResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionTagResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-tag`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionDeal(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionDealResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionDealResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-deal`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionDealUpdate(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionDealResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionDealResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-deal-update`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionPersonUpdate(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionPersonUpdateResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionPersonUpdateResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-person-update`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionCompanyUpdate(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionCompanyUpdateResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionCompanyUpdateResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-company-update`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionPerson(
  decisionEventId: string,
  expectedRecommendationId?: string,
): Promise<MaterializeCrmActionPersonResult> {
  const response = await apiFetch<{ data: MaterializeCrmActionPersonResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/materialize-person`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function materializeCrmActionQueueItem(
  materializationEndpoint: string,
  expectedRecommendationId?: string,
): Promise<unknown> {
  if (!/^\/(?:crm|v1)\/intelligence\/action-recommendations\/execution-queue\//.test(materializationEndpoint)) {
    throw new Error("Invalid CRM action materialization endpoint");
  }

  const response = await apiFetch<{ data: unknown }>(
    materializationEndpoint,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId }),
    },
  );

  return response.data;
}

export async function resolveCrmActionManual(
  decisionEventId: string,
  expectedRecommendationId?: string,
  note?: string | null,
): Promise<ResolveCrmActionManualResult> {
  const response = await apiFetch<{ data: ResolveCrmActionManualResult }>(
    `/crm/intelligence/action-recommendations/execution-queue/${decisionEventId}/resolve-manual`,
    {
      method: "POST",
      body: JSON.stringify({ expectedRecommendationId, note }),
    },
  );

  return response.data;
}

export async function bulkMaterializeCrmActionQueue(input: {
  decisionEventIds: string[];
  limit?: number;
  dryRun?: boolean;
}): Promise<BulkMaterializeCrmActionQueueResult> {
  const response = await apiFetch<{ data: BulkMaterializeCrmActionQueueResult }>(
    "/crm/intelligence/action-recommendations/execution-queue/bulk-materialize",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );

  return response.data;
}

export async function bulkResolveManualCrmActionQueue(input: {
  decisionEventIds: string[];
  limit?: number;
  dryRun?: boolean;
  note?: string | null;
}): Promise<BulkResolveManualCrmActionQueueResult> {
  const response = await apiFetch<{ data: BulkResolveManualCrmActionQueueResult }>(
    "/crm/intelligence/action-recommendations/execution-queue/bulk-resolve-manual",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );

  return response.data;
}

export function useCrmActionExecutionQueue(filters: CrmActionExecutionQueueFilters = {}) {
  const [data, setData] = useState<CrmActionExecutionQueue | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.status) {
        params.set("status", filters.status);
      }
      if (filters.priority) {
        params.set("priority", filters.priority);
      }
      const actionRef = filters.actionRef ?? filters.contractRef;
      if (actionRef) {
        params.set("actionRef", actionRef);
      }
      if (filters.sourceEventType) {
        params.set("sourceEventType", filters.sourceEventType);
      }
      if (filters.signalFamily) {
        params.set("signalFamily", filters.signalFamily);
      }
      if (filters.materializable) {
        params.set("materializable", "true");
      }
      if (filters.resolution) {
        params.set("resolution", filters.resolution);
      }
      const query = params.toString();
      const response = await apiFetch<{ data: CrmActionExecutionQueue }>(
        `/crm/intelligence/action-recommendations/execution-queue${query ? `?${query}` : ""}`,
      );
      setData(response.data);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load action execution queue");
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.actionRef,
    filters.contractRef,
    filters.materializable,
    filters.priority,
    filters.resolution,
    filters.signalFamily,
    filters.sourceEventType,
    filters.status,
  ]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchQueue,
    materializeTask: materializeCrmActionTask,
    materializeTaskUpdate: materializeCrmActionTaskUpdate,
    materializeActivity: materializeCrmActionActivity,
    materializeTag: materializeCrmActionTag,
    materializeDeal: materializeCrmActionDeal,
    materializeDealUpdate: materializeCrmActionDealUpdate,
    materializePerson: materializeCrmActionPerson,
    materializePersonUpdate: materializeCrmActionPersonUpdate,
    materializeCompanyUpdate: materializeCrmActionCompanyUpdate,
    materializeQueueItem: materializeCrmActionQueueItem,
    resolveManual: resolveCrmActionManual,
    bulkMaterialize: bulkMaterializeCrmActionQueue,
    bulkResolveManual: bulkResolveManualCrmActionQueue,
  };
}
